<template>
    <Toast />
    <ConfirmDialog />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Historial de Cierres de Caja</h5>
                        <small class="text-color-secondary">Solo el administrador puede ver este historial.</small>
                    </div>
                    <Button label="Nuevo cierre" icon="pi pi-plus" class="p-button-success mt-2 md:mt-0" @click="$router.push('/dashboard/cierre-caja')" />
                </div>

                <div class="flex flex-column md:flex-row md:justify-content-end md:align-items-end gap-2 mb-3">
                    <div>
                        <label class="block text-sm">Desde</label>
                        <Calendar v-model="filters.initialDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div>
                        <label class="block text-sm">Hasta</label>
                        <Calendar v-model="filters.endDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div>
                        <Button
                            icon="pi pi-search"
                            class="p-button-rounded apply-filter-btn"
                            v-tooltip.top="'Aplicar filtros'"
                            @click="loadList(1)"
                        />
                    </div>
                </div>

                <DataTable
                    :value="items"
                    :loading="loading"
                    :paginator="true"
                    :rows="pageSize"
                    :totalRecords="totalItems"
                    :lazy="true"
                    :first="(currentPage - 1) * pageSize"
                    @page="onPage"
                    responsiveLayout="scroll"
                    class="p-datatable-sm"
                    @row-click="onRowClick"
                >
                    <template #empty>No hay cierres registrados.</template>
                    <Column field="id" header="N" style="width:6rem" />
                    <Column field="createdAt" header="Fecha">
                        <template #body="{ data }">{{ formatDate(data.createdAt) }}</template>
                    </Column>
                    <Column field="creator" header="Realizado por">
                        <template #body="{ data }">
                            {{ data.creator }}
                            <Tag :value="data.creatorRol" severity="info" class="ml-2" />
                        </template>
                    </Column>
                    <Column header="Teórico">
                        <template #body="{ data }">{{ formatMoney(data.totalTheoretical) }}</template>
                    </Column>
                    <Column header="Práctico">
                        <template #body="{ data }">{{ formatMoney(data.totalActual) }}</template>
                    </Column>
                    <Column header="Diferencia">
                        <template #body="{ data }">
                            <span :class="resultClass(data.totalDifference)">{{ formatMoney(data.totalDifference) }}</span>
                        </template>
                    </Column>
                    <Column header="Resultado">
                        <template #body="{ data }">
                            <Tag :value="resultLabel(data.result)" :severity="resultSeverity(data.result)" />
                        </template>
                    </Column>
                    <Column header="Estado">
                        <template #body="{ data }">
                            <Tag v-if="data.status === 'OPEN'" value="Vigente" severity="success" />
                            <Tag v-else value="Anulado" severity="danger" />
                        </template>
                    </Column>
                    <Column header="" style="width:6rem">
                        <template #body="{ data }">
                            <Button icon="pi pi-eye" class="p-button-text p-button-sm" @click.stop="openDetail(data.id)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { localISODate } from '../../../service/dates';

export default {
    name: 'CashClosingList',
    data() {
        return {
            loading: false,
            items: [],
            currentPage: 1,
            pageSize: 25,
            totalPages: 1,
            totalItems: 0,
            filters: { initialDate: null, endDate: null }
        };
    },
    created() {
        if (!this.isAdmin) {
            this.$router.replace('/dashboard');
            return;
        }
        this.loadList(1);
    },
    computed: {
        isAdmin() {
            // Espejo del decorador backend: el historial de cierres es admin-only.
            return this.$can('cashClosingManage');
        }
    },
    methods: {
        async loadList(page) {
            this.loading = true;
            this.currentPage = page;
            try {
                const r = await httpRequest.getCashClosings({
                    page: page,
                    pageSize: this.pageSize,
                    initialDate: this.filters.initialDate ? this.toIso(this.filters.initialDate) : null,
                    endDate: this.filters.endDate ? this.toIso(this.filters.endDate) : null
                });
                if (r.status === 200 && r.data) {
                    this.items = r.data.items || [];
                    this.totalPages = r.data.totalPages || 1;
                    this.totalItems = r.data.totalItems || 0;
                } else if (r.status === 502) {
                    this.$toast.add({ severity: 'error', summary: 'No autorizado', life: 4000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        onPage(event) {
            this.loadList(event.page + 1);
        },
        onRowClick(event) {
            this.openDetail(event.data.id);
        },
        openDetail(id) {
            this.$router.push({ path: '/dashboard/cierre-caja/detalle', query: { id: id } });
        },
        toIso(d) {
            if (!d) return null;
            // Fecha de calendario local, no UTC: de noche toISOString() ya es «mañana» (H64).
            return localISODate(d);
        },
        formatDate(s) {
            if (!s) return '-';
            try {
                return new Date(s).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
            } catch { return s; }
        },
        formatMoney(v) {
            const n = Number(v || 0);
            return '$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        resultLabel(code) {
            return { BALANCED: 'Cuadrado', OVERAGE: 'Sobrante', SHORTAGE: 'Faltante' }[code] || code;
        },
        resultSeverity(code) {
            return { BALANCED: 'success', OVERAGE: 'info', SHORTAGE: 'danger' }[code] || 'info';
        },
        resultClass(diff) {
            const n = Number(diff || 0);
            if (n === 0) return 'text-green-500 font-medium';
            return n > 0 ? 'text-blue-500 font-medium' : 'text-red-500 font-medium';
        }
    }
};
</script>

<style scoped>
.apply-filter-btn {
    background: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
    color: var(--primary-color-text, #fff) !important;
}
.apply-filter-btn:hover {
    background: var(--primary-600, var(--primary-color)) !important;
    border-color: var(--primary-600, var(--primary-color)) !important;
}
.apply-filter-btn :deep(.p-button-icon),
.apply-filter-btn :deep(.pi) {
    color: #fff !important;
}
</style>
